import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { BUSINESS_INFO, LAST_UPDATED } from "../lib/business";
import { useLang } from "../lib/i18n";

/* -------------- shared layout -------------- */

const LegalLayout = ({ title, eyebrow, children, testid }) => (
  <div data-testid={testid} className="min-h-screen bg-black text-[color:var(--goldenelia-cream)] py-16 px-6 relative z-10">
    <div className="max-w-3xl mx-auto">
      <Link
        to="/"
        data-testid="legal-back-link"
        className="inline-flex items-center gap-2 font-display text-[10px] tracking-[0.35em] uppercase text-[color:var(--goldenelia-cream)]/65 hover:text-[color:var(--goldenelia-gold)] transition-colors mb-12"
      >
        <ArrowLeft size={14} /> Επιστροφή
      </Link>

      <p className="font-display text-[10px] tracking-[0.55em] uppercase text-[color:var(--goldenelia-gold)] mb-4">
        {eyebrow}
      </p>
      <h1 className="font-serif-luxury text-4xl sm:text-5xl gold-gradient-text mb-6 leading-tight">
        {title}
      </h1>
      <p className="font-body text-xs tracking-[0.25em] uppercase text-[color:var(--goldenelia-cream)]/45 mb-12">
        Τελευταία ενημέρωση · {LAST_UPDATED}
      </p>

      <article className="prose-legal font-body text-[color:var(--goldenelia-cream)]/80 leading-relaxed text-[15px] space-y-6">
        {children}
      </article>

      <div className="mt-16 pt-8 border-t border-[color:var(--goldenelia-gold)]/15 text-[12px] text-[color:var(--goldenelia-cream)]/55">
        <p>
          <strong className="text-[color:var(--goldenelia-gold)]">{BUSINESS_INFO.legal_name}</strong> ·
          {" "}{BUSINESS_INFO.trade_name}
        </p>
        <p>
          {BUSINESS_INFO.address}{BUSINESS_INFO.address ? " · " : ""}
          ΑΦΜ: {BUSINESS_INFO.afm} · ΔΟΥ: {BUSINESS_INFO.doy} · ΓΕΜΗ: {BUSINESS_INFO.gemi}
        </p>
        <p>Τηλ: {BUSINESS_INFO.phone} · Email: {BUSINESS_INFO.email}</p>
      </div>
    </div>
  </div>
);

const H2 = ({ children }) => (
  <h2 className="font-serif-luxury text-2xl text-[color:var(--goldenelia-gold-bright)] mt-10 mb-3">
    {children}
  </h2>
);

const P = ({ children }) => <p className="leading-[1.75]">{children}</p>;

const UL = ({ children }) => (
  <ul className="list-disc list-outside ml-6 space-y-2 leading-[1.7]">{children}</ul>
);

/* -------------- pages -------------- */

export const Privacy = () => (
  <LegalLayout testid="privacy-page" eyebrow="Νομικά" title="Πολιτική Απορρήτου & GDPR">
    <P>
      Η <strong>{BUSINESS_INFO.trade_name}</strong> ({BUSINESS_INFO.legal_name}) σέβεται την
      ιδιωτικότητά σας και συμμορφώνεται πλήρως με τον Γενικό Κανονισμό Προστασίας Δεδομένων
      (GDPR — Κανονισμός ΕΕ 2016/679) και τον Ν. 4624/2019.
    </P>

    <H2>1. Υπεύθυνος Επεξεργασίας</H2>
    <P>
      {BUSINESS_INFO.legal_name}, {BUSINESS_INFO.address}, ΑΦΜ {BUSINESS_INFO.afm}.
      Επικοινωνία για θέματα δεδομένων: <a href={`mailto:${BUSINESS_INFO.email}`} className="text-[color:var(--goldenelia-gold)] underline">{BUSINESS_INFO.email}</a>.
    </P>

    <H2>2. Δεδομένα που συλλέγουμε</H2>
    <UL>
      <li><strong>Στοιχεία παραγγελίας:</strong> ονοματεπώνυμο, τηλέφωνο, email, διεύθυνση αποστολής, σημειώσεις δώρου.</li>
      <li><strong>Στοιχεία πληρωμής:</strong> δεν αποθηκεύονται από εμάς — διαχειρίζονται μόνο από τον πάροχο πληρωμών (Viva Wallet/τράπεζα).</li>
      <li><strong>Newsletter:</strong> email και προαιρετικά γλώσσα προτίμησης.</li>
      <li><strong>Cookies/τεχνικά:</strong> session, προτίμηση γλώσσας, καλάθι, IP για ασφάλεια.</li>
    </UL>

    <H2>3. Σκοπός επεξεργασίας</H2>
    <UL>
      <li>Εκτέλεση της σύμβασης πώλησης (αποστολή προϊόντος, τιμολόγηση, εξυπηρέτηση).</li>
      <li>Συμμόρφωση με φορολογικές υποχρεώσεις (10ετής διατήρηση παραστατικών).</li>
      <li>Με τη συγκατάθεσή σας: ενημερωτικά newsletter.</li>
    </UL>

    <H2>4. Νομική βάση</H2>
    <P>
      Άρθρο 6 παρ. 1(β) GDPR (εκτέλεση σύμβασης), 6 παρ. 1(γ) (έννομη υποχρέωση — φορολογικά)
      και 6 παρ. 1(α) (συγκατάθεση — newsletter, cookies marketing).
    </P>

    <H2>5. Διατήρηση</H2>
    <UL>
      <li>Στοιχεία παραγγελίας: 10 έτη (φορολογικός νόμος).</li>
      <li>Newsletter: μέχρι να ζητήσετε διαγραφή.</li>
      <li>Cookies: όπως ορίζεται στην Πολιτική Cookies.</li>
    </UL>

    <H2>6. Διαβίβαση σε τρίτους</H2>
    <P>
      Μοιραζόμαστε δεδομένα μόνο όπου είναι απαραίτητο: εταιρείες ταχυμεταφορών (ΕΛΤΑ
      Courier/ACS/Geniki Taxydromiki), πάροχος πληρωμών (Viva Wallet), πάροχος email
      (Resend), λογιστής. Όλοι δεσμεύονται από συμβάσεις επεξεργασίας.
    </P>

    <H2>7. Τα δικαιώματά σας</H2>
    <UL>
      <li>Πρόσβαση, διόρθωση, διαγραφή («δικαίωμα στη λήθη»).</li>
      <li>Περιορισμός, εναντίωση, φορητότητα.</li>
      <li>Ανάκληση συγκατάθεσης ανά πάσα στιγμή.</li>
      <li>Καταγγελία στην Αρχή Προστασίας Δεδομένων Προσωπικού Χαρακτήρα (<a href="https://www.dpa.gr" target="_blank" rel="noreferrer" className="text-[color:var(--goldenelia-gold)] underline">www.dpa.gr</a>).</li>
    </UL>

    <H2>8. Ασφάλεια</H2>
    <P>
      Χρησιμοποιούμε κρυπτογράφηση HTTPS, hashed passwords (bcrypt), JWT tokens, brute-force
      protection και κανονικά backups.
    </P>
  </LegalLayout>
);

export const Terms = () => (
  <LegalLayout testid="terms-page" eyebrow="Νομικά" title="Όροι Χρήσης & Πώλησης">
    <P>
      Καλώς ήρθατε στο <strong>{BUSINESS_INFO.trade_name}</strong>. Η χρήση του site και η
      πραγματοποίηση παραγγελίας σημαίνει αυτόματη αποδοχή των παρόντων όρων.
    </P>

    <H2>1. Πάροχος υπηρεσιών</H2>
    <P>
      {BUSINESS_INFO.legal_name}, {BUSINESS_INFO.address}. ΑΦΜ {BUSINESS_INFO.afm} ·
      ΔΟΥ {BUSINESS_INFO.doy} · ΓΕΜΗ {BUSINESS_INFO.gemi}.
    </P>

    <H2>2. Προϊόντα & Τιμές</H2>
    <UL>
      <li>Όλες οι τιμές αναγράφονται σε <strong>ευρώ (€) με ΦΠΑ 13% / 24%</strong> ανάλογα με την κατηγορία.</li>
      <li>Οι τιμές μπορεί να αλλάξουν χωρίς προειδοποίηση — ισχύει αυτή που εμφανίζεται κατά την παραγγελία.</li>
      <li>Διατηρούμε δικαίωμα ακύρωσης παραγγελίας σε περίπτωση εμφανούς λάθους τιμής.</li>
    </UL>

    <H2>3. Διαδικασία παραγγελίας</H2>
    <UL>
      <li>Συμπληρώνετε τα στοιχεία και επιλέγετε προϊόντα στο καλάθι.</li>
      <li>Πατώντας «Ολοκλήρωση», αποστέλλεται φόρμα επιβεβαίωσης στο email σας.</li>
      <li>Η σύμβαση πώλησης ολοκληρώνεται μόνο με τη ρητή επιβεβαίωσή μας.</li>
    </UL>

    <H2>4. Πληρωμή</H2>
    <P>
      Δεκτές μέθοδοι: τραπεζική κατάθεση, αντικαταβολή (+€3), online πληρωμή με κάρτα μέσω
      ασφαλούς πύλης. Όλες οι συναλλαγές κρυπτογραφούνται με SSL.
    </P>

    <H2>5. Παράδοση</H2>
    <UL>
      <li>Εντός Ελλάδας: 1-3 εργάσιμες (Αθήνα/Θεσσαλονίκη), 2-5 εργάσιμες (νησιά/περιφέρεια).</li>
      <li>Κύπρος & Βαλκάνια: 5-10 εργάσιμες.</li>
      <li>Σε περίπτωση καθυστέρησης, σας ενημερώνουμε άμεσα.</li>
    </UL>

    <H2>6. Πνευματικά δικαιώματα</H2>
    <P>
      Το λογότυπο, τα κείμενα, οι φωτογραφίες, τα videos και ο σχεδιασμός του site
      αποτελούν πνευματική ιδιοκτησία της <strong>{BUSINESS_INFO.trade_name}</strong>.
      Απαγορεύεται η αναπαραγωγή χωρίς γραπτή άδεια.
    </P>

    <H2>7. Ευθύνη</H2>
    <P>
      Καταβάλλουμε κάθε προσπάθεια για ακρίβεια πληροφοριών αλλά δεν εγγυόμαστε αδιάλειπτη
      λειτουργία ή απουσία σφαλμάτων. Δεν ευθυνόμαστε για ζημίες από κακή χρήση των
      προϊόντων.
    </P>

    <H2>8. Εφαρμοστέο δίκαιο</H2>
    <P>
      Εφαρμοστέο το ελληνικό δίκαιο. Αρμόδια δικαστήρια Ηρακλείου Κρήτης.
      Για καταναλωτικές διαφορές: <a href="https://www.synigoroskatanaloti.gr" target="_blank" rel="noreferrer" className="text-[color:var(--goldenelia-gold)] underline">Συνήγορος Καταναλωτή</a> ή <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noreferrer" className="text-[color:var(--goldenelia-gold)] underline">Πλατφόρμα ODR της ΕΕ</a>.
    </P>
  </LegalLayout>
);

export const Refund = () => (
  <LegalLayout testid="refund-page" eyebrow="Νομικά" title="Δικαίωμα Υπαναχώρησης & Επιστροφές">
    <H2>1. 14 ημέρες υπαναχώρησης (Ν.2251/1994 & Οδηγία 2011/83/ΕΕ)</H2>
    <P>
      Έχετε δικαίωμα να υπαναχωρήσετε από τη σύμβαση εντός <strong>14 ημερολογιακών ημερών</strong>
      από την παραλαβή χωρίς αιτιολόγηση.
    </P>

    <H2>2. Εξαιρέσεις</H2>
    <UL>
      <li>Σφραγισμένα τρόφιμα/ποτά που αποσφραγίστηκαν.</li>
      <li>Φθαρτά προϊόντα (νωπό μέλι σε ανοιγμένη συσκευασία, fresh products).</li>
      <li>Προϊόντα με προσωποποίηση (engraved gift boxes με όνομα/μήνυμα).</li>
    </UL>

    <H2>3. Διαδικασία επιστροφής</H2>
    <UL>
      <li>Στείλτε email στο <a href={`mailto:${BUSINESS_INFO.email}`} className="text-[color:var(--goldenelia-gold)] underline">{BUSINESS_INFO.email}</a> με τον αριθμό παραγγελίας.</li>
      <li>Συσκευάστε το προϊόν στην αρχική του κατάσταση.</li>
      <li>Αποστείλετε στη διεύθυνση: {BUSINESS_INFO.address}.</li>
      <li>Τα έξοδα επιστροφής βαρύνουν τον πελάτη (εκτός ελαττωματικού).</li>
    </UL>

    <H2>4. Επιστροφή χρημάτων</H2>
    <P>
      Επιστρέφουμε το συνολικό ποσό εντός <strong>14 ημερών</strong> από την παραλαβή του
      προϊόντος, με τον ίδιο τρόπο που πληρώθηκε αρχικά.
    </P>

    <H2>5. Ελαττωματικά προϊόντα</H2>
    <P>
      Σε σπάσιμο μπουκαλιού, χαλασμένο μέλι ή λάθος προϊόν στην παραλαβή, επικοινωνήστε εντός
      48 ωρών με φωτογραφίες — αντικαθιστούμε δωρεάν.
    </P>
  </LegalLayout>
);

export const Cookies = () => (
  <LegalLayout testid="cookies-page" eyebrow="Νομικά" title="Πολιτική Cookies">
    <P>
      Το <strong>{BUSINESS_INFO.trade_name}</strong> χρησιμοποιεί τα ελάχιστα δυνατά cookies
      για να λειτουργήσει σωστά. Δεν χρησιμοποιούμε third-party tracking ή marketing cookies
      χωρίς ρητή συγκατάθεση.
    </P>

    <H2>Cookies που χρησιμοποιούμε</H2>
    <div className="overflow-x-auto">
      <table className="w-full border border-[color:var(--goldenelia-gold)]/20 text-[13px] mt-4">
        <thead>
          <tr className="bg-[color:var(--goldenelia-gold)]/10 text-[color:var(--goldenelia-gold)]">
            <th className="text-left p-3 font-display text-[10px] tracking-[0.2em]">Cookie</th>
            <th className="text-left p-3 font-display text-[10px] tracking-[0.2em]">Σκοπός</th>
            <th className="text-left p-3 font-display text-[10px] tracking-[0.2em]">Διάρκεια</th>
            <th className="text-left p-3 font-display text-[10px] tracking-[0.2em]">Είδος</th>
          </tr>
        </thead>
        <tbody className="text-[color:var(--goldenelia-cream)]/75">
          <tr className="border-t border-[color:var(--goldenelia-gold)]/10">
            <td className="p-3">access_token / refresh_token</td>
            <td className="p-3">Σύνδεση admin</td>
            <td className="p-3">15 λ / 7 ημ</td>
            <td className="p-3">Απαραίτητο</td>
          </tr>
          <tr className="border-t border-[color:var(--goldenelia-gold)]/10">
            <td className="p-3">goldenelia_cart (localStorage)</td>
            <td className="p-3">Καλάθι αγορών</td>
            <td className="p-3">Μόνιμο</td>
            <td className="p-3">Απαραίτητο</td>
          </tr>
          <tr className="border-t border-[color:var(--goldenelia-gold)]/10">
            <td className="p-3">goldenelia_lang</td>
            <td className="p-3">Προτίμηση γλώσσας</td>
            <td className="p-3">1 έτος</td>
            <td className="p-3">Λειτουργικό</td>
          </tr>
          <tr className="border-t border-[color:var(--goldenelia-gold)]/10">
            <td className="p-3">goldenelia_consent</td>
            <td className="p-3">Καταγραφή συγκατάθεσης</td>
            <td className="p-3">1 έτος</td>
            <td className="p-3">Απαραίτητο</td>
          </tr>
        </tbody>
      </table>
    </div>

    <H2>Ανάκληση συγκατάθεσης</H2>
    <P>
      Μπορείτε να αλλάξετε τις προτιμήσεις σας ανά πάσα στιγμή πατώντας
      «Διαχείριση Cookies» στο footer ή διαγράφοντας τα cookies από τον browser σας.
    </P>
  </LegalLayout>
);

export const Imprint = () => (
  <LegalLayout testid="imprint-page" eyebrow="Νομικά" title="Στοιχεία Επιχείρησης">
    <H2>Διακριτικός Τίτλος</H2>
    <P>{BUSINESS_INFO.trade_name}</P>

    <H2>Επωνυμία</H2>
    <P>{BUSINESS_INFO.legal_name}</P>

    <H2>Έδρα</H2>
    <P>{BUSINESS_INFO.address}, {BUSINESS_INFO.country}</P>

    <H2>Στοιχεία ΑΦΜ / ΓΕΜΗ</H2>
    <UL>
      <li>ΑΦΜ: {BUSINESS_INFO.afm}</li>
      <li>ΔΟΥ: {BUSINESS_INFO.doy}</li>
      <li>Αριθμός ΓΕΜΗ: {BUSINESS_INFO.gemi}</li>
    </UL>

    <H2>Επικοινωνία</H2>
    <UL>
      <li>Τηλέφωνο: <a href={`tel:${BUSINESS_INFO.phone}`} className="text-[color:var(--goldenelia-gold)] underline">{BUSINESS_INFO.phone}</a></li>
      <li>WhatsApp: <a href={`https://wa.me/${BUSINESS_INFO.whatsapp.replace(/[^\d]/g, "")}`} target="_blank" rel="noreferrer" className="text-[color:var(--goldenelia-gold)] underline">{BUSINESS_INFO.whatsapp}</a></li>
      <li>Email: <a href={`mailto:${BUSINESS_INFO.email}`} className="text-[color:var(--goldenelia-gold)] underline">{BUSINESS_INFO.email}</a></li>
    </UL>

    <H2>Εποπτεύουσα Αρχή</H2>
    <P>
      Επιμελητήριο Ηρακλείου, Γενική Γραμματεία Εμπορίου & Προστασίας Καταναλωτή, ΑΑΔΕ.
    </P>
  </LegalLayout>
);

/* shared exports for footer */
export const LegalLinks = () => {
  const { } = useLang();
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 font-display text-[10px] tracking-[0.3em] uppercase text-[color:var(--goldenelia-cream)]/55">
      <Link data-testid="footer-link-privacy" to="/privacy" className="hover:text-[color:var(--goldenelia-gold)]">Απόρρητο</Link>
      <Link data-testid="footer-link-terms" to="/terms" className="hover:text-[color:var(--goldenelia-gold)]">Όροι</Link>
      <Link data-testid="footer-link-refund" to="/refund" className="hover:text-[color:var(--goldenelia-gold)]">Επιστροφές</Link>
      <Link data-testid="footer-link-cookies" to="/cookies" className="hover:text-[color:var(--goldenelia-gold)]">Cookies</Link>
      <Link data-testid="footer-link-imprint" to="/imprint" className="hover:text-[color:var(--goldenelia-gold)]">Στοιχεία</Link>
    </div>
  );
};
